import { PAYMENT_METHODS_NAME } from '@autopass-web/domain';

import * as icons from './icons';

import type { ReceiptsTypeValue } from './receipts';
import type { PAYMENT_METHODS } from '@autopass-web/domain';
import type { ValueOf } from '@autopass-web/types';
import type { ReactNode } from 'react';

export type EmailFieldConfig = 'default' | 'required' | 'never';

export type ReceiptTypeConfig = Partial<
  Record<
    ReceiptsTypeValue,
    {
      emailField: EmailFieldConfig;
    }
  >
>;

type PaymentMethodsValue = ValueOf<typeof PAYMENT_METHODS>;

export const PAYMENT_METHODS_INFO = {
  credit_card: {
    key: 'CREDIT_CARD',
    value: 'credit_card',
    name: PAYMENT_METHODS_NAME['credit_card'],
    availReceiptTypes: [
      'carrier_mobile_barcode',
      'donate',
      'carrier_member',
      'b2b',
    ],
    receiptConfig: {
      b2b: {
        emailField: 'required',
      },
      carrier_member: {
        emailField: 'required',
      },
      carrier_mobile_barcode: {
        emailField: 'default',
      },
      donate: {
        emailField: 'default',
      },
    },
    Icon: icons.CreditCardIcon,
    order: 1,
    enabled: true,
  },
  apple_pay: {
    key: 'APPLE_PAY',
    value: 'apple_pay',
    name: PAYMENT_METHODS_NAME['apple_pay'],
    Icon: icons.ApplePayIcon,
    availReceiptTypes: [
      'carrier_mobile_barcode',
      'donate',
      'carrier_member',
      'b2b',
    ],
    receiptConfig: {
      b2b: {
        emailField: 'never',
      },
      carrier_member: {
        emailField: 'never',
      },
      carrier_mobile_barcode: {
        emailField: 'never',
      },
      donate: {
        emailField: 'never',
      },
    },
    order: 2,
    enabled: true,
  },
  line_pay: {
    key: 'LINE_PAY',
    value: 'line_pay',
    name: PAYMENT_METHODS_NAME['line_pay'],
    availReceiptTypes: [
      'carrier_mobile_barcode',
      'donate',
      'carrier_member',
      'b2b',
    ],
    receiptConfig: {
      b2b: {
        emailField: 'required',
      },
      carrier_member: {
        emailField: 'required',
      },
      carrier_mobile_barcode: {
        emailField: 'default',
      },
      donate: {
        emailField: 'default',
      },
    },
    Icon: icons.LinePayIcon,
    order: 3,
    enabled: true,
  },
  easy_wallet: {
    key: 'EASY_WALLET',
    value: 'easy_wallet',
    name: PAYMENT_METHODS_NAME['easy_wallet'],
    availReceiptTypes: [
      'carrier_mobile_barcode',
      'donate',
      'carrier_member',
      'b2b',
    ],
    receiptConfig: {
      b2b: {
        emailField: 'required',
      },
      carrier_member: {
        emailField: 'required',
      },
      carrier_mobile_barcode: {
        emailField: 'default',
      },
      donate: {
        emailField: 'default',
      },
    },
    Icon: icons.EasyWalletIcon,
    order: 6,
    enabled: false,
  },
  jkopay: {
    key: 'JKOPAY',
    value: 'jkopay',
    name: PAYMENT_METHODS_NAME['jkopay'],
    availReceiptTypes: [
      'carrier_mobile_barcode',
      'donate',
      'carrier_member',
      'b2b',
    ],
    receiptConfig: {
      b2b: {
        emailField: 'required',
      },
      carrier_member: {
        emailField: 'required',
      },
      carrier_mobile_barcode: {
        emailField: 'default',
      },
      donate: {
        emailField: 'default',
      },
    },
    Icon: icons.JKOPayIcon,
    order: 7,
    enabled: true,
  },
  autopass: {
    key: 'AUTOPASS',
    value: 'autopass',
    name: PAYMENT_METHODS_NAME['autopass'],
    availReceiptTypes: ['payment_carrier'],
    receiptConfig: {},
    Icon: icons.CarmochiIcon,
    order: 4,
    enabled: true,
  },
  auto_partner: {
    key: 'AUTOPASS_PARTNER',
    value: 'auto_partner',
    name: PAYMENT_METHODS_NAME['auto_partner'],
    availReceiptTypes: ['payment_carrier'],
    receiptConfig: {},
    Icon: icons.AutoPartnerIcon,
    order: 5,
    enabled: true,
  },
  taiwan_pay: {
    key: 'TAIWAN_PAY',
    value: 'taiwan_pay',
    name: PAYMENT_METHODS_NAME['taiwan_pay'],
    availReceiptTypes: [
      'carrier_mobile_barcode',
      'donate',
      'carrier_member',
      'b2b',
    ],
    receiptConfig: {
      b2b: {
        emailField: 'required',
      },
      carrier_member: {
        emailField: 'required',
      },
      carrier_mobile_barcode: {
        emailField: 'default',
      },
      donate: {
        emailField: 'default',
      },
    },
    Icon: icons.TaiwanPayIcon,
    order: 8,
    enabled: true,
  },
} as const satisfies Record<
  PaymentMethodsValue,
  {
    key: string;
    value: PaymentMethodsValue;
    name: string;
    order: number;
    availReceiptTypes: ReceiptsTypeValue[];
    receiptConfig: ReceiptTypeConfig;
    Icon: () => ReactNode;
    enabled: boolean;
  }
>;

export type PaymentMethodTypes = keyof typeof PAYMENT_METHODS_INFO;

export const paymentMethodValues = Object.keys(
  PAYMENT_METHODS_INFO,
) as PaymentMethodTypes[];

type CreditCardPaymentInfo = {
  paymentMethod: 'credit_card';
  paymentInfo: {
    number: string;
    exp: string;
    cvc: string;
  };
};

export type PaymentMethodInfo =
  | {
      paymentMethod: Exclude<PaymentMethodsValue, 'credit_card'>;
    }
  | CreditCardPaymentInfo;
